import React from 'react';

import { ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import { useErrorMessage } from '@utils/forms';
import { mergeStringObject } from '@utils/types';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  itemContainerInline: {
    ...common.flexInline,
    ...common.horizontalSpace1,
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumn,
      ...common.verticalSpace1,
      ':root &': {
        ...common.horizontalSpace0,
      },
    },
  },
  itemContainerColumn: {
    ...common.flexColumn,
    ...common.verticalSpace1,
  },
  labelContainerInline: {
    textAlign: 'right',
    minWidth: '6.0rem',
  },
  labelContainerColumn: {
    ...common.flexInline,
    flexWrap: 'wrap',
    whiteSpace: 'pre',
    color: `${theme.palette.grey['500']} !important`,
    fontSize: '0.85rem !important',
  },
  labelContainer: {
    ...common.normalText,
    fontWeight: 400,
    paddingTop: '12px',
    color: theme.palette.grey.A400,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      ...common.normalText,
      paddingTop: '5px',
    },
  },
  itemContentContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  itemHelperText: {
    ...common.smallerText,
    paddingTop: '0.5rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    color: theme.palette.error.main,
  },
  error: {},
}));

export type FormItemClasses = ClassMap<'label' | 'root' | 'itemContentContainer' | 'errorText'>;

export interface FormItemProps {
  name: string;
  label?: React.ReactNode;
  classes?: FormItemClasses;
  noLabel?: boolean;
  noErrorMessage?: boolean;
  helperText?: string;
  variant?: 'inline' | 'column';
  errorVariant?: 'inline' | 'column';
}

export const FormItem: React.FC<React.PropsWithChildren<FormItemProps>> = (props) => {
  const {
    name,
    label = <label htmlFor={name} />,
    children,
    classes: customClasses = {},
    noLabel = false,
    noErrorMessage = false,
    helperText,
    variant = 'inline',
    errorVariant = 'column',
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const errorMessage = useErrorMessage(name);

  const finalErrorMessage =
    typeof errorMessage === 'string' ? errorMessage : mergeStringObject(errorMessage);

  return (
    <div
      className={clsx(
        variant === 'inline' && classes.itemContainerInline,
        variant === 'column' && classes.itemContainerColumn,
        customClasses.root,
      )}
      id={`FormItem-${name}`}
    >
      {!noLabel && (
        <div
          className={clsx(
            variant === 'inline' && classes.labelContainerInline,
            variant === 'column' && classes.labelContainerColumn,
            classes.labelContainer,
            customClasses.label,
          )}
        >
          {label}
          {finalErrorMessage && errorVariant === 'inline' && (
            <>
              <div style={{ width: '1rem' }} />
              <div className={clsx(classes.errorText, customClasses.errorText)}>
                {finalErrorMessage}
              </div>
            </>
          )}
        </div>
      )}
      <div className={clsx(classes.flexColumn)} style={{ flex: 1, minWidth: 0 }}>
        <div className={clsx(classes.itemContentContainer, customClasses.itemContentContainer)}>
          {children}
        </div>
        {finalErrorMessage && !noErrorMessage && errorVariant === 'column' && (
          <div
            className={clsx(classes.itemHelperText, classes.errorText, customClasses.errorText)}
            role="alert"
          >
            {finalErrorMessage}
          </div>
        )}
        {helperText && <div className={clsx(classes.itemHelperText)}>{helperText}</div>}
      </div>
    </div>
  );
};
